import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import DownloadIcon from '../../static/icons/download-icon.svg';
import ShareLinkIcon from '../../static/icons/copy-icon.svg';
import BrokenLinkIcon from '../../static/icons/broken_link.svg';
import DocIcon from '../../static/img/doc.svg';
import WordDocIcon from '../../static/icons/word-doc-icon.svg';
import PdfIcon from '../../static/icons/pdf-icon.svg';
import {
  shareSubmissionDoc,
  downloadSubmission,
  downloadAllSubmissions,
  downloadSubmissionsAsZip,
} from '../../service';
import { useSharedSubmission, useAllSubmisssionsById } from '../state/hooks';
import { shouldShowShareButton, shouldShowUnshareButton } from './rules';
import CopyComponent from './CopyComponent';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import ShareActionButton from './ShareButton';
import MultiSelectDropdown from '../Shared/MultiSelectDropdown';
import DropdownMenu from '../DropdownMenu';
import { buildClassStudentsForShareDownload } from './buildClassStudents';
import Toast from '../Toast';

export { buildClassStudentsForShareDownload };

// Synthetic onboarding/tour submissions (ids prefixed 'demo-', e.g. 'demo-draft')
// have no backing document — fetching their share state 500s on the backend and
// React Query retries it 3x. Hooks run on mount even while the popup is closed,
// so the guard must live on the query itself, not on the popup render.
const isRealSubmissionId = (id) => Boolean(id) && !String(id).startsWith('demo-');

const SHARE_SCOPE = {
  THIS_STUDENT: 'THIS_STUDENT',
  ALL_RESPONSES: 'ALL_RESPONSES',
};

const DOWNLOAD_SCOPE = {
  THIS_STUDENT: 'THIS_STUDENT',
  ALL_RESPONSES: 'ALL_RESPONSES',
  SELECT_STUDENTS: 'SELECT_STUDENTS',
  ALL_STUDENTS: 'ALL_STUDENTS',
};

const FILE_FORMATS = [
  { id: 'docx', title: 'Word Document (.docx)', icon: DocIcon },
  { id: 'pdf', title: 'PDF Document (.pdf)', icon: PdfIcon },
];

export const ShareOrDownloadControl = ({
  open,
  onClose,
  submission,
  students = [],
  assignmentId,
  variant = 'combined',
}) => {
  const isBulkDownload = variant === 'bulk';
  const { data: sharedSubmission } = useSharedSubmission(
    submission?.id,
    open && !isBulkDownload && isRealSubmissionId(submission?.id)
  );
  const queryClient = useQueryClient();
  const resolvedAssignmentId =
    assignmentId || submission?.assignmentId || submission?.assignment?.id;
  const { data: batchSubmissions, isLoadingdata: isLoadingStudents } = useAllSubmisssionsById(
    resolvedAssignmentId,
    Boolean(open && isBulkDownload && resolvedAssignmentId && !submission?.studentsSubmissions?.length),
  );
  const availableSubmissions = useMemo(() => submission?.studentsSubmissions?.length
    ? submission.studentsSubmissions
    : (batchSubmissions || []).map((row) => ({ ...row, submissionId: row.submissionId || row.id })),
  [submission?.studentsSubmissions, batchSubmissions]);

  const [shareScope, setShareScope] = useState(SHARE_SCOPE.THIS_STUDENT);
  const [downloadScope, setDownloadScope] = useState(
    isBulkDownload ? DOWNLOAD_SCOPE.ALL_STUDENTS : DOWNLOAD_SCOPE.THIS_STUDENT,
  );
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [fileFormat, setFileFormat] = useState('docx');
  const [isDownloading, setIsDownloading] = useState(false);
  const selectedFormatIndex = Math.max(
    0,
    FILE_FORMATS.findIndex((format) => format.id === fileFormat),
  );

  const studentOptions = useMemo(
    () =>
      buildClassStudentsForShareDownload(students, availableSubmissions).map(
        (student) => ({
          value: `student-${student.id}`,
          label: student.name,
          submissionIds: student.submissionIds,
        }),
      ),
    [students, availableSubmissions],
  );
  const hasStudents = studentOptions.length > 0;

  const selectedSubmissionIds = useMemo(() => {
    const ids = selectedStudentIds
      .flatMap(
        (value) =>
          studentOptions.find((option) => option.value === value)?.submissionIds || [],
      )
      .filter(Boolean);

    // Warn if some selected students don't have submissions
    const missingCount = selectedStudentIds.filter((value) =>
      !studentOptions.find((option) => option.value === value)?.submissionIds.length
    ).length;
    if (missingCount > 0 && selectedStudentIds.length > 0) {
      const studentWord = missingCount === 1 ? 'student' : 'students';
      const hasWord = missingCount === 1 ? 'has' : 'have';
      console.warn(
        `${missingCount} selected ${studentWord} ${hasWord} no submissions and will be excluded from download`
      );
    }

    return [...new Set(ids)];
  }, [selectedStudentIds, studentOptions]);

  const shareMutation = useMutation({
    mutationFn: ({ submissionId, shareType, sharedWith = [] }) =>
      shareSubmissionDoc(submissionId, sharedWith, shareType),
    onMutate: async ({ submissionId, shareType }) => {
      await queryClient.cancelQueries({
        queryKey: ['getsharedSubmission', submissionId],
      });
      const previousSubmission = queryClient.getQueryData([
        'getsharedSubmission',
        submissionId,
      ]);
      queryClient.setQueryData(['getsharedSubmission', submissionId], (old) => ({
        ...old,
        sharedWith: shareType,
      }));
      return { previousSubmission };
    },
    onSuccess: (_, { submissionId }) => {
      queryClient.invalidateQueries({
        queryKey: ['getsharedSubmission', submissionId],
      });
    },
  });

  const shareUrl = useMemo(() => {
    if (shareScope === SHARE_SCOPE.ALL_RESPONSES && resolvedAssignmentId) {
      return `${window.location.origin}/#/tasks/${resolvedAssignmentId}`;
    }
    return `${window.location.origin}/#/view/submissions/${submission?.id}`;
  }, [shareScope, resolvedAssignmentId, submission?.id]);

  const handleDialogClick = (e) => {
    e.stopPropagation();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleShareLink = () => {
    if (!submission?.id) return;
    shareMutation.mutate({
      submissionId: submission.id,
      shareType: 'ANYONE_WITH_LINK',
    });
  };

  const handleDeactivateLink = () => {
    if (!submission?.id) return;
    shareMutation.mutate({
      submissionId: submission.id,
      shareType: 'NO_ONE',
    });
  };

  const handleDownload = async () => {
    if (isDownloading || (!isBulkDownload && !submission?.id)) return;
    setIsDownloading(true);
    try {
      if (
        downloadScope === DOWNLOAD_SCOPE.ALL_STUDENTS ||
        downloadScope === DOWNLOAD_SCOPE.ALL_RESPONSES
      ) {
        if (!resolvedAssignmentId) {
          toast(
            <Toast
              message="Cannot download all responses: assignment not found"
              error
            />
          );
          setIsDownloading(false);
          return;
        }
        await downloadAllSubmissions(resolvedAssignmentId, fileFormat);
      } else if (downloadScope === DOWNLOAD_SCOPE.SELECT_STUDENTS) {
        // Warn if some students were excluded
        const missingCount = selectedStudentIds.filter((value) =>
          !studentOptions.find((option) => option.value === value)?.submissionIds.length
        ).length;
        if (missingCount > 0) {
          const studentWord = missingCount === 1 ? 'student' : 'students';
          const hasWord = missingCount === 1 ? 'has' : 'have';
          toast(
            <Toast
              message={`Note: ${missingCount} selected ${studentWord} ${hasWord} no submissions and will be excluded`}
            />
          );
        }

        await downloadSubmissionsAsZip(
          selectedSubmissionIds,
          `submissions-${resolvedAssignmentId || 'selected'}`,
          fileFormat,
        );
      } else {
        await downloadSubmission(submission.id, fileFormat);
      }
      onClose();
    } catch (error) {
      console.error('Error downloading document:', error);
      toast(
        <Toast
          message={`Download failed: ${error.message || 'Unknown error'}`}
          error
        />
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const isSelectStudentsDownload =
    downloadScope === DOWNLOAD_SCOPE.SELECT_STUDENTS;
  const canDownload =
    !isDownloading &&
    (isBulkDownload ? Boolean(resolvedAssignmentId) : Boolean(submission?.id)) &&
    (!isSelectStudentsDownload || selectedSubmissionIds.length > 0);

  if (!open) return null;

  return (
    <DialogOverlay onClick={handleOverlayClick} data-testid="share-overlay">
      <DialogContainer
        onClick={handleDialogClick}
        data-testid="share-container"
      >
        {!isBulkDownload && (
          <>
            <DialogHeading>Share this document</DialogHeading>

            <SectionSubtitle>Share using link</SectionSubtitle>
            <OptionsGroup>
              <ScopeOption
                type="button"
                $selected={shareScope === SHARE_SCOPE.THIS_STUDENT}
                onClick={() => setShareScope(SHARE_SCOPE.THIS_STUDENT)}
              >
                <ScopeRadio $checked={shareScope === SHARE_SCOPE.THIS_STUDENT} />
                <ScopeLabel>Only this student</ScopeLabel>
              </ScopeOption>
              {hasStudents && (
                <ScopeOption
                  type="button"
                  $selected={shareScope === SHARE_SCOPE.ALL_RESPONSES}
                  onClick={() => setShareScope(SHARE_SCOPE.ALL_RESPONSES)}
                >
                  <ScopeRadio $checked={shareScope === SHARE_SCOPE.ALL_RESPONSES} />
                  <ScopeLabel>All responses</ScopeLabel>
                </ScopeOption>
              )}
            </OptionsGroup>

            {shouldShowShareButton(sharedSubmission) && (
              <ShareActionButton
                onClick={handleShareLink}
                icon={ShareLinkIcon}
                text="Share link"
                data-testid="share-link-button"
              />
            )}

            {shouldShowUnshareButton(sharedSubmission) && (
              <>
                <CopyRow>
                  <CopyComponent
                    textToCopy={shareUrl}
                    showInput
                    activeBorder
                    data-testid="share-url-field"
                  />
                </CopyRow>
                <ShareActionButton
                  onClick={handleDeactivateLink}
                  icon={BrokenLinkIcon}
                  text="Deactivate Link"
                  secondary
                  data-testid="deactivate-link-button"
                />
                <DeactivateWarning>
                  Anyone with this link will no longer be able to view this...
                </DeactivateWarning>
              </>
            )}

            <Divider />
          </>
        )}

        <DialogHeading>
          {isBulkDownload ? 'Download this document' : 'Download document'}
        </DialogHeading>
        {isBulkDownload && isLoadingStudents && <p role="status">Loading students…</p>}
        {(!isBulkDownload || hasStudents) && (
          <>
            <SectionSubtitle>Download type</SectionSubtitle>
            <OptionsGroup>
              {isBulkDownload ? (
                <>
                  <ScopeOption
                    type="button"
                    $selected={downloadScope === DOWNLOAD_SCOPE.ALL_STUDENTS}
                    onClick={() => setDownloadScope(DOWNLOAD_SCOPE.ALL_STUDENTS)}
                  >
                    <ScopeRadio
                      $checked={downloadScope === DOWNLOAD_SCOPE.ALL_STUDENTS}
                    />
                    <ScopeLabel>Individual files for all students</ScopeLabel>
                  </ScopeOption>
                  <SelectStudentsBlock
                    $selected={downloadScope === DOWNLOAD_SCOPE.SELECT_STUDENTS}
                    $bulk
                  >
                    <ScopeOption
                      type="button"
                      $selected={downloadScope === DOWNLOAD_SCOPE.SELECT_STUDENTS}
                      $nested
                      onClick={() =>
                        setDownloadScope(DOWNLOAD_SCOPE.SELECT_STUDENTS)
                      }
                      $inline
                    >
                      <ScopeRadio
                        $checked={downloadScope === DOWNLOAD_SCOPE.SELECT_STUDENTS}
                      />
                      <ScopeLabel>Select students</ScopeLabel>
                    </ScopeOption>
                    <StudentSelectWrap>
                        <MultiSelectDropdown
                          options={studentOptions}
                          selectedValues={selectedStudentIds}
                          onChange={(values) => {
                            setDownloadScope(DOWNLOAD_SCOPE.SELECT_STUDENTS);
                            setSelectedStudentIds(values);
                          }}
                          placeholder="Select"
                          getOptionLabel={(opt) => opt.label}
                          getOptionValue={(opt) => opt.value}
                          alwaysWhite
                          showCheckboxes
                        />
                        {selectedStudentIds.length > 0 && (
                          <SelectionInfo>
                            {selectedSubmissionIds.length}{' '}
                            {selectedSubmissionIds.length === 1 ? 'response' : 'responses'} selected
                            {selectedSubmissionIds.length === 0 && (
                              <WarningText> - Cannot download</WarningText>
                            )}
                          </SelectionInfo>
                        )}
                    </StudentSelectWrap>
                  </SelectStudentsBlock>
                </>
              ) : (
                <>
                  <ScopeOption
                    type="button"
                    $selected={downloadScope === DOWNLOAD_SCOPE.THIS_STUDENT}
                    onClick={() => setDownloadScope(DOWNLOAD_SCOPE.THIS_STUDENT)}
                  >
                    <ScopeRadio
                      $checked={downloadScope === DOWNLOAD_SCOPE.THIS_STUDENT}
                    />
                    <ScopeLabel>Only this student</ScopeLabel>
                  </ScopeOption>
                  {hasStudents && (
                    <>
                      <ScopeOption
                        type="button"
                        $selected={downloadScope === DOWNLOAD_SCOPE.ALL_RESPONSES}
                        onClick={() => setDownloadScope(DOWNLOAD_SCOPE.ALL_RESPONSES)}
                      >
                        <ScopeRadio
                          $checked={downloadScope === DOWNLOAD_SCOPE.ALL_RESPONSES}
                        />
                        <ScopeLabel>All responses</ScopeLabel>
                      </ScopeOption>
                      <SelectStudentsBlock
                        $selected={downloadScope === DOWNLOAD_SCOPE.SELECT_STUDENTS}
                      >
                        <ScopeOption
                          type="button"
                          $selected={downloadScope === DOWNLOAD_SCOPE.SELECT_STUDENTS}
                          $nested={downloadScope === DOWNLOAD_SCOPE.SELECT_STUDENTS}
                          onClick={() =>
                            setDownloadScope(DOWNLOAD_SCOPE.SELECT_STUDENTS)
                          }
                          $inline
                        >
                          <ScopeRadio
                            $checked={downloadScope === DOWNLOAD_SCOPE.SELECT_STUDENTS}
                          />
                          <ScopeLabel>Select students</ScopeLabel>
                        </ScopeOption>
                        {downloadScope === DOWNLOAD_SCOPE.SELECT_STUDENTS && (
                          <StudentSelectWrap>
                            <MultiSelectDropdown
                              options={studentOptions}
                              selectedValues={selectedStudentIds}
                              onChange={setSelectedStudentIds}
                              placeholder="Select"
                              getOptionLabel={(opt) => opt.label}
                              getOptionValue={(opt) => opt.value}
                              alwaysWhite
                              showCheckboxes
                            />
                            {selectedStudentIds.length > 0 && (
                              <SelectionInfo>
                                {selectedSubmissionIds.length} of {selectedStudentIds.length} selected{' '}
                                {selectedStudentIds.length === 1 ? 'student has' : 'students have'}{' '}
                                submissions
                                {selectedSubmissionIds.length === 0 && (
                                  <WarningText> - Cannot download</WarningText>
                                )}
                              </SelectionInfo>
                            )}
                          </StudentSelectWrap>
                        )}
                      </SelectStudentsBlock>
                    </>
                  )}
                </>
              )}
            </OptionsGroup>
          </>
        )}

        <SectionSubtitle>File format</SectionSubtitle>
        <FormatDropdownWrap data-testid="file-format-select">
          <DropdownMenu
            key={`file-format-${fileFormat}`}
            fullWidth
            borderColor="rgba(201, 198, 204, 0.85)"
            borderRadius="8px"
            leadingIcon={isBulkDownload && fileFormat === 'docx'
              ? WordDocIcon : FILE_FORMATS[selectedFormatIndex].icon}
            menuItems={FILE_FORMATS}
            selectedIndex={selectedFormatIndex}
            onItemSelected={(item) => setFileFormat(item.id)}
          />
        </FormatDropdownWrap>

        <ShareActionButton
          $bulkDownload={isBulkDownload}
          onClick={handleDownload}
          icon={DownloadIcon}
          text={isDownloading ? 'Downloading...' : 'Download'}
          disabled={!canDownload}
          data-testid="download-button"
        />
      </DialogContainer>
    </DialogOverlay>
  );
};

const DialogOverlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(30, 16, 72, 0.45);
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const DialogContainer = styled.div`
  width: min(468px, 100%);
  max-height: min(90vh, 940px);
  overflow-y: auto;
  border-radius: 12px;
  padding: 30px;
  background-color: #fff;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.22);
  font-family: 'IBM Plex Sans', sans-serif;
`;

const DialogHeading = styled.h3`
  margin: 0 0 20px;
  font-weight: 600;
  font-size: 24px;
  line-height: 1.3;
  color: #1e252a;
`;

const SectionSubtitle = styled.p`
  font-size: 16px;
  font-weight: 500;
  margin: 0 0 12px;
  color: #4b464f;
`;

const OptionsGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
`;

const ScopeOption = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 52px;
  padding: 14px 12px;
  box-sizing: border-box;
  border-radius: 8px;
  border: ${({ $nested, $selected }) =>
    $nested
      ? 'none'
      : `1.5px solid ${$selected ? '#7200e0' : 'rgba(201, 198, 204, 0.85)'}`};
  background: ${({ $nested, $selected }) =>
    $nested ? 'transparent' : $selected ? '#fbf7fe' : '#fff'};
  cursor: pointer;
  text-align: left;
  margin-bottom: ${({ $inline }) => ($inline ? '12px' : '0')};
`;

const ScopeRadio = styled.span`
  width: 17px;
  height: 17px;
  border-radius: 50%;
  border: 2px solid ${({ $checked }) => ($checked ? '#7200e0' : '#c9c6cc')};
  flex-shrink: 0;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: 3px;
    border-radius: 50%;
    background: ${({ $checked }) => ($checked ? '#7200e0' : 'transparent')};
  }
`;

const ScopeLabel = styled.span`
  font-size: 16px;
  color: #1e252a;
`;

const SelectStudentsBlock = styled.div`
  border-radius: 8px;
  border: 1.5px solid
    ${({ $selected, $bulk }) => ($selected ? '#7200e0' : $bulk ? 'rgba(201, 198, 204, 0.85)' : 'transparent')};
  background: ${({ $selected }) => ($selected ? '#fbf7fe' : 'transparent')};
  padding: ${({ $selected, $bulk }) => ($selected || $bulk ? '12px' : '0')};
`;

const StudentSelectWrap = styled.div`
  padding: 0 4px 4px;
`;

const CopyRow = styled.div`
  margin-top: 12px;
`;

const Divider = styled.div`
  height: 1px;
  background-color: rgba(201, 198, 204, 0.8);
  margin: 28px 0;
`;

const DeactivateWarning = styled.p`
  font-size: 12px;
  font-style: italic;
  color: #ba2f25;
  text-align: center;
  margin: 12px 4px 0;
  line-height: 1.4;
`;

const FormatDropdownWrap = styled.div`
  width: 100%;
  margin-bottom: 16px;
`;

const SelectionInfo = styled.p`
  margin: 8px 0 0;
  font-size: 13px;
  color: #4b464f;
  line-height: 1.4;
`;

const WarningText = styled.span`
  color: #ba2f25;
  font-weight: 500;
`;
